import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ChevronRight } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import FloatingWhatsApp from "@/components/FloatingWhatsApp";
import useReveal from "@/hooks/useReveal";
import { api } from "@/lib/api";
import { trackPageView, track } from "@/lib/analytics";

export default function SeriesIndex() {
  const [series, setSeries] = useState([]);
  const [loading, setLoading] = useState(true);
  const r = useReveal();

  useEffect(() => {
    trackPageView();
    api
      .get("/series")
      .then(({ data }) => setSeries(data || []))
      .catch(() => setSeries([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="series-index-page" className="bg-[#FAFAFA] min-h-screen">
      <Navbar />
      <main className="pt-32 pb-24">
        <section className="max-w-7xl mx-auto px-6 md:px-8">
          <div ref={r} className="reveal max-w-3xl">
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-10 bg-[#C5A059]" aria-hidden />
              <span className="text-[0.7rem] uppercase tracking-[0.3em] text-[#C5A059] font-medium">
                Séries de Sermões
              </span>
            </div>
            <h1 className="font-serif-display text-[#0B1E36] text-4xl md:text-6xl lg:text-[5rem] leading-[1.0] tracking-[-0.032em] font-medium">
              Estude a Palavra como{" "}
              <span className="italic text-[#C5A059]">jornada</span>.
            </h1>
            <p className="mt-8 text-[1.05rem] md:text-[1.2rem] text-[#6B7280] leading-[1.85] font-light">
              Coleções temáticas que unem dezenas de sermões em uma sequência
              progressiva. Comece pelo primeiro episódio e avance no seu ritmo.
            </p>
          </div>

          {loading ? (
            <div className="mt-20 grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="aspect-[4/3] bg-[#F5F7FA] animate-pulse" />
              ))}
            </div>
          ) : series.length === 0 ? (
            <p className="mt-16 text-[#6B7280]">Nenhuma série disponível ainda.</p>
          ) : (
            <div
              data-testid="series-list"
              className="mt-20 grid md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-14"
            >
              {series.map((s, i) => (
                <Link
                  key={s.id}
                  to={`/series/${s.category_slug}/${s.slug}`}
                  data-testid={`series-card-${s.slug}`}
                  onClick={() => track("click_series", { slug: s.slug, category: s.category_slug })}
                  className="group block"
                >
                  <div className="relative aspect-[4/5] bg-[#0B1E36] overflow-hidden">
                    <div
                      className="absolute inset-0"
                      style={{
                        background:
                          "radial-gradient(ellipse at 30% 25%, rgba(197,160,89,0.22) 0%, transparent 60%), linear-gradient(155deg, #0B1E36 0%, #13294B 60%, #0B1E36 100%)",
                      }}
                      aria-hidden
                    />
                    <div className="absolute inset-5 border border-[#C5A059]/20" />
                    <div className="relative h-full flex flex-col p-8">
                      <div className="flex items-center justify-between">
                        <span className="font-serif-display text-[2.5rem] text-[#C5A059] leading-none">
                          {String(s.series_order).padStart(2, "0")}
                        </span>
                        <span className="text-[0.62rem] uppercase tracking-[0.3em] text-white/55">
                          {s.sermon_count} episódios
                        </span>
                      </div>
                      <div className="mt-auto">
                        <p className="text-[0.65rem] uppercase tracking-[0.28em] text-[#C5A059] font-medium mb-4">
                          Série
                        </p>
                        <h3 className="font-serif-display italic text-white text-[1.45rem] md:text-[1.65rem] leading-[1.2] tracking-[-0.012em]">
                          {s.title}
                        </h3>
                      </div>
                    </div>
                  </div>
                  <p className="mt-5 text-[#6B7280] text-[0.95rem] leading-[1.7] font-light">
                    {s.description}
                  </p>
                  <span className="mt-4 inline-flex items-center gap-2 text-[0.7rem] uppercase tracking-[0.24em] text-[#0B1E36]/70 group-hover:text-[#C5A059] transition-colors duration-300 border-b border-[#0B1E36]/10 group-hover:border-[#C5A059]/60 pb-1.5">
                    Acessar a série
                    <ArrowUpRight
                      size={12}
                      strokeWidth={1.7}
                      className="opacity-70 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
                    />
                  </span>
                </Link>
              ))}
            </div>
          )}
        </section>
      </main>
      <Footer />
      <FloatingWhatsApp />
    </div>
  );
}
